<template>
  <div class="revenue-class-shell">
    <!-- HERO -->
    <section class="revenue-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Revenue by Class</h1>
        <p class="hero-copy">
          See what is carrying the room: glasses, bottles, reserves, plates, and whatever else hits the counter.
        </p>
      </div>

      <button @click="fetchReport" class="primary-action">
        Generate Report
      </button>
    </section>

    <!-- DATE CONTROLS -->
    <section class="revenue-toolbar">
      <label>
        <span>Start Date</span>
        <input type="date" v-model="startDate" />
      </label>

      <label>
        <span>End Date</span>
        <input type="date" v-model="endDate" />
      </label>

      <button type="button" @click="setToday" class="ghost-action">
        Today
      </button>

      <button type="button" @click="setThisMonth" class="ghost-action">
        This Month
      </button>

      <button type="button" @click="setLast30Days" class="ghost-action">
        Last 30
      </button>
    </section>

    <!-- SUMMARY -->
    <section class="summary-grid">
      <article class="summary-card">
        <span>Gross Revenue</span>
        <strong>${{ money(totalRevenue) }}</strong>
      </article>

      <article class="summary-card">
        <span>Refunds / Returns</span>
        <strong class="negative">-${{ money(totalRefunds) }}</strong>
      </article>

      <article class="summary-card">
        <span>Net Revenue</span>
        <strong>${{ money(totalNetRevenue) }}</strong>
      </article>

      <article class="summary-card">
        <span>Best Class</span>
        <strong>{{ bestClassLabel }}</strong>
      </article>
    </section>

    <!-- VISUALS -->
    <section class="visual-grid">
      <!-- PIE -->
      <article class="chart-card">
        <div class="section-heading">
          <p class="eyebrow">Mix</p>
          <h2>Revenue Split</h2>
        </div>

        <div v-if="classes.length" class="pie-wrap">
          <div
            class="css-pie"
            :style="{ background: pieGradient }"
          >
            <div class="pie-hole">
              <span>Net</span>
              <strong>${{ money(totalNetRevenue) }}</strong>
            </div>
          </div>

          <div class="legend-list">
            <button
              v-for="row in classes"
              :key="row.sale_mode"
              class="legend-row"
              type="button"
              @click="openClassDetails(row)"
            >
              <span
                class="legend-dot"
                :style="{ background: classColor(row.sale_mode) }"
              ></span>

              <span>{{ displaySaleMode(row.sale_mode) }}</span>

              <strong>{{ percent(row.revenue_percent) }}</strong>
            </button>
          </div>
        </div>

        <div v-else class="empty-chart">
          No revenue data for this period.
        </div>
      </article>

      <!-- BAR CHART -->
      <article class="chart-card">
        <div class="section-heading">
          <p class="eyebrow">Rank</p>
          <h2>Class Performance</h2>
        </div>

        <div v-if="classes.length" class="bar-list">
          <button
            v-for="row in classes"
            :key="`bar-${row.sale_mode}`"
            class="bar-row"
            type="button"
            @click="openClassDetails(row)"
          >
            <div class="bar-row-top">
              <span>{{ displaySaleMode(row.sale_mode) }}</span>
              <strong>${{ money(row.net_revenue ?? row.revenue) }}</strong>
            </div>

            <div class="bar-track">
              <div
                class="bar-fill"
                :style="{
                  width: `${barWidth(row.net_revenue ?? row.revenue)}%`,
                  background: classColor(row.sale_mode)
                }"
              ></div>
            </div>

            <div class="bar-meta">
              <span>{{ number(row.units_sold) }} units</span>
              <span v-if="Number(row.refund_total || 0) > 0">
                -${{ money(row.refund_total) }} refunds
              </span>
              <span>${{ money(row.gross_profit) }} gross profit</span>
            </div>
          </button>
        </div>

        <div v-else class="empty-chart">
          Ring some sales, then come back here.
        </div>
      </article>
    </section>

    <!-- CLASS CARDS -->
    <section v-if="classes.length" class="class-grid">
      <article
        v-for="row in classes"
        :key="`card-${row.sale_mode}`"
        class="class-card"
        @click="openClassDetails(row)"
      >
        <div class="class-top">
          <div>
            <p class="label">{{ displaySaleMode(row.sale_mode) }}</p>
            <h2>${{ money(row.net_revenue ?? row.revenue) }}</h2>
          </div>

          <span
            class="class-orb"
            :style="{ background: classColor(row.sale_mode) }"
          ></span>
        </div>

        <div class="class-stats">
          <div>
            <span>Units</span>
            <strong>{{ number(row.units_sold) }}</strong>
          </div>

          <div>
            <span>Refunds</span>
            <strong class="negative">-${{ money(row.refund_total) }}</strong>
          </div>

          <div>
            <span>Margin</span>
            <strong>{{ grossMarginPercent(row) }}</strong>
          </div>
        </div>

        <div class="card-footer">
          <span>{{ percent(row.revenue_percent) }} of net revenue</span>
          <strong>
            {{ Number(row.adjustment_count || 0) > 0 ? 'Adjusted →' : 'Details →' }}
          </strong>
        </div>
      </article>
    </section>

    <section v-else class="empty-state">
      <p class="eyebrow">No Sales Mix Yet</p>
      <h2>No revenue by class found.</h2>
      <p>Pick a date range with sales, then generate the report.</p>
    </section>

    <!-- DETAIL MODAL -->
    <div v-if="showDetailPopup" class="modal-overlay">
      <div class="revenue-modal">
        <div
          class="modal-topline"
          :style="{ background: classColor(selectedClass.sale_mode) }"
        ></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Class Detail</p>
            <h2>{{ displaySaleMode(selectedClass.sale_mode) }}</h2>
            <p>
              {{ classNarrative(selectedClass) }}
            </p>
          </div>

          <button type="button" @click="showDetailPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="detail-hero">
            <div>
              <span>Gross Revenue</span>
              <strong>${{ money(selectedClass.revenue) }}</strong>
            </div>

            <div>
              <span>Refunds</span>
              <strong class="negative">-${{ money(selectedClass.refund_total) }}</strong>
            </div>

            <div>
              <span>Net Revenue</span>
              <strong>${{ money(selectedClass.net_revenue ?? selectedClass.revenue) }}</strong>
            </div>
          </section>

          <section class="detail-grid">
            <div>
              <span>Units Sold</span>
              <strong>{{ number(selectedClass.units_sold) }}</strong>
            </div>

            <div>
              <span>Line Count</span>
              <strong>{{ number(selectedClass.line_count) }}</strong>
            </div>

            <div>
              <span>COGS</span>
              <strong>${{ money(selectedClass.cogs) }}</strong>
            </div>

            <div>
              <span>Gross Margin</span>
              <strong>{{ grossMarginPercent(selectedClass) }}</strong>
            </div>

            <div>
              <span>Average Revenue / Unit</span>
              <strong>${{ money(avgRevenuePerUnit(selectedClass)) }}</strong>
            </div>

            <div>
              <span>Average COGS / Unit</span>
              <strong>${{ money(avgCogsPerUnit(selectedClass)) }}</strong>
            </div>

            <div>
              <span>Returned to Stock</span>
              <strong>{{ number(selectedClass.returned_inventory_qty) }}</strong>
            </div>

            <div>
              <span>Dumped / Faulty</span>
              <strong>{{ number(selectedClass.dumped_faulty_qty) }}</strong>
            </div>
          </section>

          <section class="detail-note">
            <span>Readout</span>
            <p>{{ detailReadout(selectedClass) }}</p>
          </section>

          <footer class="modal-actions">
            <button type="button" class="primary-action" @click="showDetailPopup = false">
              Close
            </button>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'RevenueByClassReport',

  data() {
    return {
      startDate: '',
      endDate: '',

      total_revenue: 0,
      classes: [],

      showDetailPopup: false,
      selectedClass: {}
    };
  },

  computed: {
    totalRevenue() {
      return Number(this.total_revenue || 0);
    },

    totalCogs() {
      return this.classes.reduce((sum, row) => {
        return sum + Number(row.cogs || 0);
      }, 0);
    },

    totalGrossProfit() {
      return this.classes.reduce((sum, row) => {
        return sum + Number(row.gross_profit || 0);
      }, 0);
    },

    bestClass() {
      if (!this.classes.length) {
        return null;
      }

      return this.classes.slice().sort((a, b) => {
        return Number(b.net_revenue ?? b.revenue ?? 0) -
          Number(a.net_revenue ?? a.revenue ?? 0);
      })[0];
    },
    bestClassLabel() {
      return this.bestClass ? this.displaySaleMode(this.bestClass.sale_mode) : '—';
    },

    maxRevenue() {
      if (!this.classes.length) {
        return 0;
      }

      return Math.max(...this.classes.map(row => Number(row.net_revenue ?? row.revenue ?? 0)));
    },
    totalRefunds() {
      return this.classes.reduce((sum, row) => {
        return sum + Number(row.refund_total || 0);
      }, 0);
    },

    totalNetRevenue() {
      return this.classes.reduce((sum, row) => {
        return sum + Number(row.net_revenue ?? row.revenue ?? 0);
      }, 0);
    },

    pieGradient() {
      if (!this.classes.length || this.totalNetRevenue <= 0) {
        return 'rgba(255,255,255,0.08)';
      }

      let cursor = 0;

      const segments = this.classes.map(row => {
        const percent = Number(row.revenue_percent || 0);
        const start = cursor;
        const end = cursor + percent;
        cursor = end;

        return `${this.classColor(row.sale_mode)} ${start}% ${end}%`;
      });

      return `conic-gradient(${segments.join(', ')})`;
    }
  },

  methods: {
    async fetchReport() {
      try {
        const response = await api.get('/reports/revenue-by-class', {
          params: {
            start_date: this.startDate || null,
            end_date: this.endDate || null
          }
        });

        const payload = response.data || {};

        this.total_revenue = Number(payload.total_revenue || 0);

        this.classes = (payload.classes || []).map(row => ({
          ...row,
          line_count: Number(row.line_count || 0),
          units_sold: Number(row.units_sold || 0),

          revenue: Number(row.revenue || 0),
          refund_total: Number(row.refund_total || 0),
          net_revenue: Number(row.net_revenue ?? row.revenue ?? 0),

          cogs: Number(row.cogs || 0),
          gross_profit: Number(row.gross_profit || 0),

          revenue_percent: Number(row.revenue_percent || 0),
          adjustment_count: Number(row.adjustment_count || 0),
          returned_inventory_qty: Number(row.returned_inventory_qty || 0),
          dumped_faulty_qty: Number(row.dumped_faulty_qty || 0)
        }));
      } catch (error) {
        console.error('❌ Error fetching revenue by class report:', error);
        alert('Failed to fetch Revenue by Class report. Check backend logs.');
      }
    },

    setToday() {
      const today = new Date().toISOString().split('T')[0];
      this.startDate = today;
      this.endDate = today;
      this.fetchReport();
    },

    setThisMonth() {
      const now = new Date();

      this.startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .split('T')[0];

      this.endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];

      this.fetchReport();
    },

    setLast30Days() {
      const now = new Date();
      const start = new Date();
      start.setDate(now.getDate() - 30);

      this.startDate = start.toISOString().split('T')[0];
      this.endDate = now.toISOString().split('T')[0];

      this.fetchReport();
    },

    openClassDetails(row) {
      this.selectedClass = row;
      this.showDetailPopup = true;
    },

    displaySaleMode(mode) {
      const labels = {
        GLASS: 'Glasses',
        BOTTLE: 'Full Bottles',
        RESERVE_BOTTLE: 'Reserve Bottles',
        PLATE: 'Plates',
        MIXED: 'Mixed',
        UNKNOWN: 'Unknown'
      };

      return labels[mode] || mode || 'Unknown';
    },

    classColor(mode) {
      const colors = {
        GLASS: '#c24a2f',
        BOTTLE: '#c8a66a',
        RESERVE_BOTTLE: '#8f123f',
        PLATE: '#2faa76',
        MIXED: '#6f7bd9',
        UNKNOWN: '#8f8682'
      };

      return colors[mode] || '#8f8682';
    },

    barWidth(value) {
      if (!this.maxRevenue) {
        return 0;
      }

      return Math.max(4, (Number(value || 0) / this.maxRevenue) * 100);
    },

    avgRevenuePerUnit(row) {
      const units = Number(row.units_sold || 0);
      const revenue = Number(row.net_revenue ?? row.revenue ?? 0);

      return units > 0 ? revenue / units : 0;
    },

    avgCogsPerUnit(row) {
      const units = Number(row.units_sold || 0);
      return units > 0 ? Number(row.cogs || 0) / units : 0;
    },

    grossMarginPercent(row) {
      const revenue = Number(row.net_revenue ?? row.revenue ?? 0);
      const grossProfit = Number(row.gross_profit || 0);

      if (revenue <= 0) {
        return 'N/A';
      }

      return `${((grossProfit / revenue) * 100).toFixed(2)}%`;
    },

    classNarrative(row) {
      const label = this.displaySaleMode(row.sale_mode);
      const share = this.percent(row.revenue_percent);
      const refunds = Number(row.refund_total || 0);

      if (refunds > 0) {
        return `${label} made up ${share} of net revenue after $${this.money(refunds)} in refunds or returns.`;
      }

      return `${label} made up ${share} of net revenue in this period.`;
    },

    detailReadout(row) {

      const refunds = Number(row.refund_total || 0);
      const adjustmentNote = refunds > 0
        ? ` This class also had $${this.money(refunds)} in refunds/returns.`
        : '';

      const mode = row.sale_mode;
      const margin = this.grossMarginPercent(row);

      if (mode === 'GLASS') {
        return `Glass pours are the heartbeat of the room. Watch this against bottle and reserve sales to see whether the nightly program is carrying the bar. Current gross margin: ${margin}.${adjustmentNote}`;
      }

      if (mode === 'BOTTLE') {
        return `Full bottles usually mean groups, bigger tickets, and faster inventory movement. Current gross margin: ${margin}.${adjustmentNote}`;
      }

      if (mode === 'RESERVE_BOTTLE') {
        return `Reserve bottles are your high-touch, high-story sales. They may not move every night, but they can change the shape of the evening. Current gross margin: ${margin}.${adjustmentNote}`;
      }

      if (mode === 'PLATE') {
        return `Plates add revenue without wine inventory depletion. Once BOM is added, this section will show true food margin. Current gross margin: ${margin}.${adjustmentNote}`;
      }

      return `This class contributed to the period revenue. Current gross margin: ${margin}.${adjustmentNote}`;
    },

    money(value) {
      return Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    number(value) {
      return Number(value || 0).toLocaleString('en-US', {
        maximumFractionDigits: 4
      });
    },

    percent(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '0.00%';
      }

      return `${Number(value || 0).toFixed(2)}%`;
    }
  },

  mounted() {
    this.setToday();
  }
};
</script>

<style scoped>
.revenue-class-shell {
  min-height: 100vh;
  padding: 28px;
  color: #f7efe8;
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.38), transparent 34%),
    radial-gradient(circle at bottom right, rgba(194, 74, 47, 0.18), transparent 32%),
    linear-gradient(135deg, #0d090d 0%, #171016 42%, #24141b 100%);
}

.revenue-hero,
.revenue-toolbar,
.summary-card,
.chart-card,
.class-card,
.empty-state,
.revenue-modal {
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(18px);
}

.revenue-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 30px;
}

.eyebrow,
.label {
  margin: 0 0 8px;
  color: #c8a66a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  font-weight: 900;
}

.label {
  color: #9b8f8b;
  letter-spacing: 0.12em;
}

.revenue-hero h1 {
  margin: 0;
  font-size: clamp(42px, 7vw, 78px);
  line-height: 0.92;
  letter-spacing: -0.07em;
}

.hero-copy {
  max-width: 720px;
  margin: 16px 0 0;
  color: #cfc2bd;
  font-size: 16px;
  line-height: 1.5;
}

.revenue-toolbar {
  display: grid;
  grid-template-columns: 1fr 1fr auto auto auto;
  gap: 12px;
  align-items: end;
  margin-top: 18px;
  padding: 18px;
}

.revenue-toolbar label {
  display: grid;
  gap: 8px;
}

.revenue-toolbar label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.revenue-class-shell input {
  min-height: 52px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  outline: 0;
  background: rgba(6, 4, 6, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  font-size: 15px;
  font-family: inherit;
}

.revenue-class-shell input:focus {
  border-color: rgba(200, 166, 106, 0.65);
  box-shadow: 0 0 0 3px rgba(200, 166, 106, 0.14);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(170px, 1fr));
  gap: 14px;
  margin-top: 18px;
}

.summary-card {
  padding: 20px;
}

.summary-card span {
  display: block;
  color: #a99d98;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.summary-card strong {
  display: block;
  margin-top: 10px;
  font-size: 32px;
  line-height: 1;
  letter-spacing: -0.05em;
}

.visual-grid {
  display: grid;
  grid-template-columns: minmax(340px, 0.9fr) minmax(420px, 1.1fr);
  gap: 18px;
  margin-top: 18px;
}

.chart-card {
  padding: 24px;
}

.section-heading h2 {
  margin: 0;
  font-size: clamp(30px, 4vw, 48px);
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.pie-wrap {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 22px;
  align-items: center;
  margin-top: 24px;
}

.css-pie {
  width: 260px;
  height: 260px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  box-shadow:
    0 30px 60px rgba(0, 0, 0, 0.36),
    inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.pie-hole {
  width: 142px;
  height: 142px;
  display: grid;
  place-items: center;
  align-content: center;
  border-radius: 50%;
  background: #171016;
  border: 1px solid rgba(255, 255, 255, 0.09);
  text-align: center;
}

.pie-hole span {
  color: #9b8f8b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.14em;
}

.pie-hole strong {
  margin-top: 4px;
  color: #f1c979;
  font-size: 20px;
  letter-spacing: -0.04em;
}

.legend-list {
  display: grid;
  gap: 10px;
}

.legend-row {
  min-height: 48px;
  display: grid;
  grid-template-columns: 14px 1fr auto;
  gap: 10px;
  align-items: center;
  border: 0;
  border-radius: 16px;
  padding: 0 12px;
  color: #fff8f1;
  background: rgba(0, 0, 0, 0.22);
  cursor: pointer;
  text-align: left;
}

.legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 999px;
}

.legend-row strong {
  color: #f1c979;
}

.bar-list {
  display: grid;
  gap: 14px;
  margin-top: 22px;
}

.bar-row {
  display: grid;
  gap: 10px;
  border: 0;
  border-radius: 20px;
  padding: 16px;
  color: #fff8f1;
  background: rgba(0, 0, 0, 0.22);
  cursor: pointer;
  text-align: left;
}

.bar-row:hover,
.legend-row:hover,
.class-card:hover {
  transform: translateY(-2px);
  border-color: rgba(200, 166, 106, 0.32);
}

.bar-row-top,
.bar-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.bar-row-top span {
  font-weight: 900;
  font-size: 18px;
}

.bar-row-top strong {
  color: #f1c979;
}

.bar-meta {
  color: #a99d98;
  font-size: 13px;
  font-weight: 800;
}

.bar-track {
  height: 13px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

.bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s ease;
}

.class-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 16px;
  margin-top: 18px;
}

.class-card {
  padding: 20px;
  cursor: pointer;
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease,
    border-color 0.16s ease;
}

.class-top {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.class-top h2 {
  margin: 0;
  font-size: clamp(28px, 4vw, 46px);
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.class-orb {
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  border-radius: 18px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.28);
}

.class-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 11px;
  margin-top: 16px;
}

.class-stats div,
.detail-grid div,
.detail-hero div {
  padding: 14px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.065);
}

.class-stats span,
.detail-grid span,
.detail-hero span,
.detail-note span {
  display: block;
  margin-bottom: 6px;
  color: #9b8f8b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.class-stats strong,
.detail-grid strong,
.detail-hero strong {
  color: #fff8f1;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  color: #cfc2bd;
}

.card-footer strong {
  color: #f1c979;
}

.empty-chart,
.empty-state {
  margin-top: 18px;
  padding: 34px;
  border-radius: 24px;
  text-align: center;
  color: #cfc2bd;
  background: rgba(0, 0, 0, 0.22);
  border: 1px dashed rgba(255, 255, 255, 0.14);
}

.empty-state h2 {
  margin: 0;
  font-size: clamp(32px, 5vw, 54px);
  letter-spacing: -0.06em;
}

.primary-action,
.ghost-action {
  min-height: 50px;
  padding: 0 18px;
  border-radius: 999px;
  border: 0;
  color: #fff8f1;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease;
}

.primary-action {
  background: linear-gradient(135deg, #8f123f, #c24a2f);
  box-shadow: 0 14px 32px rgba(194, 74, 47, 0.3);
}

.ghost-action {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
}

.primary-action:hover,
.ghost-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
}

/* ---------- Modal ---------- */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.revenue-modal {
  width: min(780px, 94vw);
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
  border-radius: 30px;
}

.modal-topline {
  height: 5px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: 42px;
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  padding: 24px 28px 28px;
  overflow-y: auto;
}

.detail-hero {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.detail-hero strong {
  color: #f1c979;
  font-size: 28px;
  letter-spacing: -0.05em;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 14px;
}

.detail-note {
  margin-top: 14px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.065);
}

.detail-note p {
  margin: 0;
  color: #cfc2bd;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.negative {
  color: #ff9f8e !important;
}

.class-card:has(.negative) {
  border-color: rgba(255, 159, 142, 0.18);
}

/* ---------- Responsive ---------- */

@media (max-width: 1150px) {
  .visual-grid,
  .class-grid {
    grid-template-columns: 1fr;
  }

  .pie-wrap {
    grid-template-columns: 1fr;
    justify-items: center;
  }

  .legend-list {
    width: 100%;
  }
}

@media (max-width: 850px) {
  .revenue-class-shell {
    padding: 16px;
  }

  .revenue-hero {
    flex-direction: column;
    align-items: stretch;
  }

  .revenue-toolbar,
  .summary-grid,
  .class-stats,
  .detail-hero,
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .bar-row-top,
  .bar-meta,
  .card-footer {
    flex-direction: column;
  }

  .css-pie {
    width: 220px;
    height: 220px;
  }

  .pie-hole {
    width: 122px;
    height: 122px;
  }

  .modal-overlay {
    padding: 12px;
    align-items: stretch;
  }

  .revenue-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 24px;
  }

  .modal-header {
    flex-direction: column;
  }

  .primary-action,
  .ghost-action {
    width: 100%;
  }
}
</style>